import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Configurazione worker una sola volta globalmente
if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.legacy.js';
}

const LoadingSpinner = () => (
    <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '3rem',
        gap: '1rem'
    }}>
        <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid var(--accent, #4361ee)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: '#666', fontSize: '1rem' }}>Caricamento PDF...</p>
        <style dangerouslySetInnerHTML={{
            __html: `
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `
        }} />
    </div>
);

const ErrorFallback = ({ error, onRetry, fileUrl }) => (
    <div style={{
        padding: '3rem',
        textAlign: 'center',
        background: '#fff5f5',
        borderRadius: '12px',
        border: '1px solid #fed7d7',
        margin: '2rem'
    }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
        <h3 style={{ color: '#e53e3e', marginBottom: '1rem' }}>
            Errore nel caricamento del PDF
        </h3>
        <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            {error?.message?.includes('Invalid PDF') ?
                'Il file PDF sembra essere danneggiato o non valido.' :
                error?.message?.includes('Worker') ?
                    'Errore temporaneo del visualizzatore PDF.' :
                    'Non è stato possibile caricare il documento.'
            }
        </p>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
                onClick={onRetry}
                style={{
                    background: 'var(--accent, #4361ee)',
                    color: 'white',
                    border: 'none',
                    padding: '0.7rem 1.5rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                }}
            >
                🔄 Riprova
            </button>
        </div>
    </div>
);

export const OptimizedPDFViewer = ({
    fileUrl,
    onLoadSuccess,
    onLoadError,
    width = '100%',
    height = 'auto',
    isMobile = false,
    initialScale = 1.0
}) => {
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [scale, setScale] = useState(1.0);
    const [pageWidth, setPageWidth] = useState(null);
    const containerRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Funzione per calcolare lo scale ottimale
    const calculateOptimalScale = useCallback(() => {
        if (!containerRef.current || !pageWidth) return 1.0;

        const containerWidth = containerRef.current.clientWidth;
        const padding = 40; // 20px per lato
        const availableWidth = containerWidth - padding;

        // Calcola scale per adattarsi alla larghezza disponibile
        const widthScale = availableWidth / pageWidth;

        // Limita lo scale tra 0.5 e 2.0 per evitare estremi
        return Math.min(Math.max(widthScale, 0.5), 2.0);
    }, [pageWidth]);

    // Aggiorna scale quando cambiano le dimensioni
    useEffect(() => {
        const updateScale = () => {
            const newScale = calculateOptimalScale();
            if (newScale !== scale) {
                setScale(newScale);
            }
        };

        const resizeObserver = new ResizeObserver(updateScale);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        updateScale(); // Calcola immediatamente

        return () => {
            resizeObserver.disconnect();
        };
    }, [calculateOptimalScale, scale]);

    // Crea URL unico per evitare cache issues
    const documentUrl = useMemo(() => {
        if (!fileUrl) return null;
        return fileUrl + (fileUrl.includes('?') ? '&' : '?') + `t=${Date.now()}&retry=${retryCount}`;
    }, [fileUrl, retryCount]);

    const handleLoadSuccess = useCallback(async (pdf) => {
        if (!mountedRef.current) return;
        setNumPages(pdf.numPages);

        // Ottieni dimensioni della prima pagina per calcolare scale
        try {
            const firstPage = await pdf.getPage(1);
            const viewport = firstPage.getViewport({ scale: 1.0 });
            setPageWidth(viewport.width);
        } catch (err) {
            console.warn('Could not get page dimensions:', err);
        }

        setLoading(false);
        setError(null);
        onLoadSuccess?.(pdf);
    }, [onLoadSuccess]);

    const handleLoadError = useCallback((err) => {
        if (!mountedRef.current) return;
        console.error('PDF Load Error:', err);
        setLoading(false);
        setError(err);
        onLoadError?.(err);
    }, [onLoadError]);

    const handleRetry = useCallback(() => {
        if (!mountedRef.current) return;
        setError(null);
        setLoading(true);
        setRetryCount(prev => prev + 1);
    }, []);

    // Auto-retry per worker errors
    useEffect(() => {
        if (error?.message?.includes('Worker was destroyed') && retryCount < 2) {
            const timer = setTimeout(handleRetry, 1500);
            return () => clearTimeout(timer);
        }
    }, [error, retryCount, handleRetry]);

    // Reset state quando cambia URL
    useEffect(() => {
        setError(null);
        setLoading(true);
        setCurrentPage(1);
        setRetryCount(0);
    }, [fileUrl]);

    if (!fileUrl) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                Nessun documento disponibile
            </div>
        );
    }

    if (error) {
        return <ErrorFallback error={error} onRetry={handleRetry} fileUrl={fileUrl} />;
    }

    return (
        <div ref={containerRef} style={{ width, height, background: '#f8f9fa' }}>
            {loading && <LoadingSpinner />}

            <Document
                file={documentUrl}
                onLoadSuccess={handleLoadSuccess}
                onLoadError={handleLoadError}
                loading=""
                error=""
                options={{
                    cMapUrl: 'https://unpkg.com/pdfjs-dist@2.16.105/cmaps/',
                    cMapPacked: true,
                    standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@2.16.105/standard_fonts/',
                    useWorkerFetch: false,
                    isEvalSupported: false,
                    useSystemFonts: true
                }}
            >
                {numPages > 0 && (
                    <div>
                        {/* Controls */}
                        {numPages > 1 && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '1rem',
                                padding: '1rem',
                                background: 'white',
                                borderBottom: '1px solid #e2e8f0',
                                position: 'sticky',
                                top: 0,
                                zIndex: 100
                            }}>
                                <button
                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage <= 1}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: currentPage <= 1 ? '#e2e8f0' : 'var(--accent, #4361ee)',
                                        color: currentPage <= 1 ? '#666' : 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: currentPage <= 1 ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    ← Precedente
                                </button>

                                <span style={{ color: '#666', fontSize: '0.9rem' }}>
                                    Pagina {currentPage} di {numPages}
                                </span>

                                <button
                                    onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
                                    disabled={currentPage >= numPages}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: currentPage >= numPages ? '#e2e8f0' : 'var(--accent, #4361ee)',
                                        color: currentPage >= numPages ? '#666' : 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: currentPage >= numPages ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Successiva →
                                </button>
                            </div>
                        )}

                        {/* PDF Page */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '1rem',
                            minHeight: '400px'
                        }}>
                            <Page
                                pageNumber={currentPage}
                                scale={scale}
                                loading={<LoadingSpinner />}
                                error={
                                    <div style={{ padding: '2rem', color: '#e53e3e' }}>
                                        Errore nel caricamento della pagina {currentPage}
                                    </div>
                                }
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                            />
                        </div>
                    </div>
                )}
            </Document>
        </div>
    );
};